/**
 * base-element.ts —— 所有 lx-* 组件的基类
 *
 * 职责：
 *   1. 统一 attachShadow + adoptedStyleSheets 注入样式
 *      （比 <style> 标签性能好，且多个同类实例共享同一份 CSSStyleSheet）
 *   2. 自动解绑事件订阅 —— 组件卸载时清理，防内存泄漏
 *   3. 渲染调度：属性变更 → 微任务合并 → 只渲染一次
 *
 * 约定：
 *   - 子类实现 render() 返回 HTML 字符串，基类负责写入 shadow root
 *   - 子类实现 styles() 返回 CSS 文本
 *   - 子类若需在渲染后操作 DOM / 绑事件，实现 onMounted()
 */

import { eventBus, type Unsubscribe } from './event-bus';
import type { AppEventMap } from '@/types/events';
import { sheetCache, baseResetSheet } from '@/styles/sheet-cache';

export abstract class LxElement extends HTMLElement {
  /** 本实例持有的所有取消订阅函数，卸载时统一调用 */
  private _subscriptions: Unsubscribe[] = [];
  /** 是否已经完成首次渲染 */
  private _mounted = false;
  /** 待执行的渲染任务（用于合并多次请求） */
  private _renderScheduled = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  /* ==========================================================
     子类需要实现的钩子
     ========================================================== */

  /** 返回 shadow DOM 的 HTML 结构 */
  protected abstract render(): string;

  /** 返回组件样式（CSS 文本）。默认返回空字符串 */
  protected styles(): string {
    return '';
  }

  /** 渲染完成后的回调，用于操作 DOM、绑定事件。每次渲染后都会调用 */
  protected onMounted(): void {}

  /** 组件卸载时的回调 */
  protected onUnmounted(): void {}

  /* ==========================================================
     生命周期
     ========================================================== */

  connectedCallback(): void {
    // 首次连接时才注入样式 —— 只做一次，后续渲染复用
    if (!this._mounted) {
      const own = this.styles();
      // 第一张是所有组件共享的基础重置（box-sizing / 元素 reset / 滚动条）
      const sheets = [baseResetSheet()];
      if (own) sheets.push(sheetCache.get(this.tagName, () => own));
      this.shadowRoot!.adoptedStyleSheets = sheets;
      this._mounted = true;
    }

    this.update();

    // 监听 auto 主题下系统配色变化
    this._mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this._mediaQuery.addEventListener('change', this._onSystemThemeChange);
  }

  disconnectedCallback(): void {
    this._subscriptions.forEach((off) => off());
    this._subscriptions = [];
    this._mediaQuery?.removeEventListener('change', this._onSystemThemeChange);
    this.onUnmounted();
  }

  private _mediaQuery: MediaQueryList | null = null;

  private _onSystemThemeChange = (): void => {
    // 由主题模块统一处理，这里仅作为兜底触发一次渲染
    this.update();
  };

  /* ==========================================================
     订阅与渲染工具
     ========================================================== */

  /**
   * 订阅全局事件，组件卸载时自动解绑。
   * 子类直接在 connectedCallback 之后的任意时机调用即可。
   */
  protected subscribe<K extends keyof AppEventMap>(
    event: K,
    handler: (payload: AppEventMap[K]) => void,
  ): void {
    this._subscriptions.push(eventBus.on(event, handler));
  }

  /** 广播事件 */
  protected emit<K extends keyof AppEventMap>(
    event: K,
    payload: AppEventMap[K],
  ): void {
    eventBus.emit(event, payload);
  }

  /**
   * 请求渲染。多次连续调用会被合并成一次（微任务级别），
   * 避免一次性修改多个属性时重复渲染。
   */
  protected update(): void {
    if (this._renderScheduled) return;
    this._renderScheduled = true;

    queueMicrotask(() => {
      this._renderScheduled = false;
      if (!this.isConnected) return;
      this.shadowRoot!.innerHTML = this.render();
      this.onMounted();
    });
  }

  /** 查询 shadow DOM 内的元素 */
  protected $(selector: string): HTMLElement | null {
    return this.shadowRoot!.querySelector(selector);
  }

  protected $$(selector: string): HTMLElement[] {
    return Array.from(this.shadowRoot!.querySelectorAll(selector));
  }

  /** 绑定点击事件（会自动绑定 this） */
  protected onClick(selector: string, handler: (e: Event) => void): void {
    this.$(selector)?.addEventListener('click', handler);
  }

  /* ==========================================================
     小工具：HTML 转义
     ========================================================== */

  /** 转义 HTML 特殊字符，防止注入。所有插值文本都应经过它 */
  protected esc(text: unknown): string {
    return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

/** 注册自定义元素，重复注册时静默跳过（支持 HMR 热更新场景） */
export function define(tagName: string, ctor: CustomElementConstructor): void {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, ctor);
  }
}
